<template>
  <v-card variant="outlined" class="lx-drivers">
    <div class="lx-panel__head">
      <span class="lx-panel__title">What drives this forecast</span>
      <span class="lx-panel__meta">
        <label class="lx-ctl">
          week
          <select class="form-control minimal" :value="week" aria-label="Week for the decomposition" @change="$emit('update:week', Number($event.target.value))">
            <option v-for="w in weeks" :key="w" :value="w">{{ w }}</option>
          </select>
        </label>
      </span>
    </div>
    <div class="lx-panel__body">
      <div v-if="!block" class="lx-state lx-state--partial" role="status">
        <span class="lx-state__title">Driver decomposition not available</span>
        <p class="lx-state__msg">{{ reason || "The backend did not provide a driver decomposition for this series." }}</p>
      </div>

      <template v-else>
        <p class="lx-narrative" aria-live="polite"><template v-for="(s, i) in sentences" :key="i">{{ s }} </template></p>

        <div class="lx-wf mt-4" role="table" aria-label="Driver waterfall">
          <template v-for="(r, i) in rows" :key="i">
            <div class="lx-wf__label" :class="{ 'lx-wf__total': r.kind === 'total' }" role="cell">{{ r.label }}</div>
            <div class="lx-wf__track" role="cell" :aria-label="r.label + ' ' + valueText(r)">
              <template v-if="r.kind === 'total'">
                <div class="lx-wf__bar lx-wf__bar--total" :style="totalStyle(r)"></div>
              </template>
              <template v-else>
                <div class="lx-wf__zero" :style="{ left: pct(0) + '%' }"></div>
                <div class="lx-wf__bar" :class="'lx-wf__bar--' + r.kind" :style="barStyle(r)"></div>
              </template>
            </div>
            <div class="lx-wf__val" :class="{ 'lx-wf__total': r.kind === 'total' }" role="cell">{{ valueText(r) }}</div>
          </template>
        </div>

        <p class="lx-method mt-3">
          Σ contributions {{ fmtDiff(rec.sum) }} · scenario change {{ fmtDiff(rec.total) }} · residual {{ fmtDiff(rec.residual) }}
          (tolerance {{ fmtDiff(rec.tolerance) }}) ·
          <span v-if="rec.reconciled">reconciles</span><span v-else class="lx-neg">does not reconcile — residual shown as its own bar</span>
          · basis: {{ basisText }}
        </p>

        <!-- coefficients -->
        <section class="lx-section mt-4" :class="{ 'lx-section--closed': !showCoef }">
          <button type="button" class="lx-section__head" :aria-expanded="showCoef" @click="showCoef = !showCoef">
            <span class="lx-section__title">Coefficients · {{ series }}</span>
            <span class="lx-section__meta"><template v-if="equation">{{ equation.spec }} · {{ equation.estimator }}</template><template v-else>not provided</template> <span class="ml-2">{{ showCoef ? "−" : "+" }}</span></span>
          </button>
          <div v-show="showCoef" class="lx-section__body lx-panel__body--flush">
            <div v-if="!equation" class="lx-method pa-3">No equation metadata was provided for this series.</div>
            <div v-else class="lx-scroll-x">
              <table class="lx-mtable" aria-label="Coefficient table">
                <thead>
                  <tr>
                    <th scope="col">Input</th><th scope="col">Transform</th><th scope="col" class="num">Estimate</th><th scope="col" class="num">Std. err.</th><th scope="col" class="num">t</th><th scope="col">Expected sign</th><th scope="col">Interpretation</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="c in coefRows" :key="c.input" :class="{ sub: c.estimate === 0 }">
                    <td>{{ c.input }}</td>
                    <td>{{ c.transform || "—" }}</td>
                    <td class="num">{{ c.estimate == null ? "n/a" : fmtNum(c.estimate, 4) }}</td>
                    <td class="num"><template v-if="c.stdErr != null">{{ fmtNum(c.stdErr, 4) }}</template><span v-else class="na" title="Not available for regularised estimators">n/a</span></td>
                    <td class="num"><template v-if="c.tStat != null">{{ fmtNum(c.tStat, 2) }}</template><span v-else class="na">n/a</span></td>
                    <td>
                      <template v-if="c.expectedSign">{{ c.expectedSign }}<span v-if="contradicts(c)" class="lx-neg"> · estimated sign contradicts the prior</span></template>
                      <span v-else class="na">no prior</span>
                    </td>
                    <td>{{ interpret(c) }}</td>
                  </tr>
                </tbody>
              </table>
              <p class="lx-method pa-3">{{ equation.note || "" }} Coefficients describe the fitted equation; they are not causal effects unless an identification strategy supports it. Rows with a zero estimate were dropped by the regulariser.</p>
            </div>
          </div>
        </section>
      </template>
    </div>
  </v-card>
</template>

<script>
import { reconcile, waterfallRows, driverAxis } from "../lib/decomposition";
import { narrative } from "../lib/narrative";
import { formatValue, formatDiff, formatNumber, kindOf, unitOf } from "../lib/format";

export default {
  name: "ForecastDrivers",
  props: {
    block: { type: Object, default: null }, // drivers doc for the series/week
    series: { type: String, default: "" },
    week: { type: Number, default: 52 },
    weeks: { type: Array, default: () => [4, 13, 26, 39, 52] },
    scenario: { type: String, default: "alternative" }, // alternative | baseline
    equation: { type: Object, default: null }, // diagnostics equation for the series
    reason: { type: String, default: "" },
  },
  emits: ["update:week"],
  data: () => ({ showCoef: false }),
  computed: {
    rec() {
      return reconcile(this.block);
    },
    rows() {
      return waterfallRows(this.rec);
    },
    axis() {
      return driverAxis(this.rows);
    },
    totalRange() {
      const t = this.rows.filter((r) => r.kind === "total").map((r) => r.value);
      const lo = Math.min(0, ...t);
      const hi = Math.max(0, ...t);
      return { min: lo, max: hi === lo ? lo + 1 : hi };
    },
    sentences() {
      return narrative(this.rec, { series: this.series, week: this.week, scenario: this.scenario, basis: this.block && this.block.basis });
    },
    basisText() {
      const b = this.block && this.block.basis;
      if (b === "model-equations") return "model equations and the weekly interpolation";
      if (b === "fixture-elasticities") return "illustrative fixture elasticities (not H2F2)";
      if (b === "baseline-path") return "baseline path";
      return b || "not stated";
    },
    coefRows() {
      const rows = (this.equation && this.equation.coefficients) || [];
      return rows.slice().sort((a, b) => Math.abs(Number(b.estimate) || 0) - Math.abs(Number(a.estimate) || 0));
    },
  },
  methods: {
    pct(v) {
      const { min, max } = this.axis;
      return ((v - min) / (max - min)) * 100;
    },
    barStyle(r) {
      const a = this.pct(Math.min(0, r.value));
      const b = this.pct(Math.max(0, r.value));
      return { left: a + "%", width: Math.max(0.5, b - a) + "%" };
    },
    totalStyle(r) {
      const { min, max } = this.totalRange;
      const w = ((r.value - min) / (max - min)) * 100;
      return { left: "0%", width: Math.max(0.5, w) + "%" };
    },
    valueText(r) {
      return r.kind === "total" ? formatValue(this.series, r.value) : formatDiff(this.series, r.value);
    },
    fmtDiff(v) {
      return formatDiff(this.series, v);
    },
    fmtNum(v, d) {
      return formatNumber(v, d);
    },
    contradicts(c) {
      if (!c.expectedSign || c.estimate == null || c.estimate === 0) return false;
      return (c.expectedSign === "+" && c.estimate < 0) || (c.expectedSign === "-" && c.estimate > 0);
    },
    interpret(c) {
      if (c.estimate == null) return "n/a";
      if (c.estimate === 0) return "Dropped by the regulariser (zero weight).";
      if (/const|dummy/i.test(c.input)) return c.input === "const" ? "Intercept." : "Regime dummy (shift in the equation's level).";
      const target = this.equation && this.equation.target ? this.equation.target : this.series;
      const unit = kindOf(this.series) === "cds" ? "bp" : unitOf(this.series) || "units";
      const tdesc = /levels/.test(this.equation && this.equation.spec) ? `the level of ${target}` : `the weekly change of ${target}`;
      const dir = c.estimate > 0 ? "increase" : "decrease";
      return `A one-unit rise in ${c.base || c.input} (${c.transform || "level"}) is associated with a ${formatNumber(Math.abs(c.estimate), 3)} ${unit} ${dir} in ${tdesc}, other inputs held constant, within this equation.`;
    },
  },
};
</script>
